import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ConfigProvider, notification } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faDownload } from "@fortawesome/free-solid-svg-icons";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  fetchMerchantData,
  setTimeRange,
  setLastUpdated,
} from "./store/merchantAnalyticsSlice";
import { fetchOverviewData, fetchSalesTrendsData, fetchCategoriesData, fetchTopItemsData } from "./store/merchantAnalyticsOverviewSlice";
import SalesTrendChart from "./Graphs/SalesTrendChart";
import CategoryDistributionChart from "./Graphs/CategoryDistributionChart";
import TopItemsChart from "./Graphs/TopItemsChart";
import AnalyticsTile from "./AnalyticsTile";
import { TimeRange, AnalyticsTile as AnalyticsTileType } from "./Constant";
import API from "../../../context/API";
import { useTheme } from "../../../context/ThemeContext";
import { getAntdThemeConfig } from "../../../utils/antdTheme";
import {
  COLOR_MAP,
  OVERVIEW_TILES_CONFIG,
  TIME_RANGE_OPTIONS,
  DEFAULT_TAB_KEY,
  TAB_CONFIG,
  NAVIGATION_ROUTES,
  MESSAGES,
  CHART_CONFIGS,
  getDateRange,
  formatTimeAgo,
  getTileValue,
  downloadFile,
} from "./Constant";
import { ChartCardWrapper, renderSelectedTile, hasTileComponent } from "./utils";
import {
  Container,
  HeaderRow,
  HeaderContent,
  TitleSection,
  MainTitle,
  Subtitle,
  ControlsRow,
  StyledButton,
  TilesGrid,
  StyledTabs,
  StyledAlert,
} from "./styles";
import CustomSelector from "../../../commonComponents/Selector";
import { Col } from "antd";

interface MerchantAnalyticsProps {
  role?: string;
}

const MerchantAnalytics: React.FC<MerchantAnalyticsProps> = ({ role }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";

  const {
    merchantData,
    analyticsData,
    loading,
    analyticsLoading,
    lastUpdated,
    timeRange,
  } = useSelector((state: RootState) => state.merchantAnalytics);
  const {
    overviewData,
    salesTrendsData,
    categoriesData,
    topItemsData,
    loading: overviewLoading,
    salesTrendsLoading,
    categoriesLoading,
    topItemsLoading,
  } = useSelector((state: RootState) => state.merchantAnalyticsOverview);
  const [selectedTile, setSelectedTile] = useState<AnalyticsTileType | null>(
    null
  );
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB_KEY);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    if (role === "merchant" && !merchantData) {
      dispatch(fetchMerchantData()).then((result) => {
        if (fetchMerchantData.fulfilled.match(result)) {
          const now = Date.now();
          localStorage.setItem("lastUpdatedTimestamp", now.toString());
          dispatch(setLastUpdated(formatTimeAgo(new Date(now))));
        } else {
          notification.error({
            message: "Error",
            description: MESSAGES.FETCH_ERROR,
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  useEffect(() => {
    if (role === "merchant" || role === "admin") {
      const { startDate, endDate } = getDateRange(timeRange);
      dispatch(fetchOverviewData({ startDate, endDate }));
      dispatch(fetchSalesTrendsData({ startDate, endDate }));
      dispatch(fetchCategoriesData({ startDate, endDate }));
      dispatch(fetchTopItemsData({ startDate, endDate }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, timeRange]);


  const handleTimeRangeChange = (newRange: string) => {
    dispatch(setTimeRange(newRange as TimeRange));
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const { startDate, endDate } = getDateRange(timeRange);

      const response = await API.get(`/analytics/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { startDate, endDate, format: "csv" },
        responseType: "blob",
      });

      downloadFile(
        new Blob([response.data]),
        `analytics-${startDate}-to-${endDate}.csv`
      );
      notification.success({
        message: "Success",
        description: MESSAGES.EXPORT_SUCCESS,
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      notification.error({
        message: "Error",
        description: MESSAGES.EXPORT_ERROR,
      });
    } finally {
      setExportLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshLoading(true);
    try {
      const { startDate, endDate } = getDateRange(timeRange);
      await Promise.all([
        dispatch(fetchOverviewData({ startDate, endDate })),
        dispatch(fetchSalesTrendsData({ startDate, endDate })),
        dispatch(fetchCategoriesData({ startDate, endDate })),
        dispatch(fetchTopItemsData({ startDate, endDate })),
      ]);
      notification.info({
        message: "Info",
        description: MESSAGES.REFRESH_INFO,
      });
    } catch (error) {
      console.error("Error refreshing data:", error);
      notification.error({
        message: "Error",
        description: "Failed to refresh data",
      });
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleTileClick = (tile: AnalyticsTileType | { id: string }) => {
    const route = NAVIGATION_ROUTES[tile.id];
    if (route) {
      navigate(route);
      return;
    }    
    if (hasTileComponent(tile.id)) {
      setSelectedTile(tile as AnalyticsTileType);
    }
  };

  const overviewTiles: AnalyticsTileType[] = useMemo(() => {
    return OVERVIEW_TILES_CONFIG.map((config) => {
      const { value, change, progress } = getTileValue(config.id, analyticsData, overviewData);
      return {...config, value, change, progress};
    });
  }, [analyticsData, overviewData]);

  if (selectedTile) {
    return renderSelectedTile(selectedTile.id) || null;
  }

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? "dark" : "light" }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <div>
            <HeaderRow>
              <HeaderContent>
                <TitleSection>
                  <MainTitle level={2}>Analytics Dashboard</MainTitle>
                  <Subtitle>
                    Track your restaurant's performance and insights
                  </Subtitle>
                </TitleSection>
              </HeaderContent>
              <ControlsRow>
                <CustomSelector
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  options={TIME_RANGE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  width={160}
                  height={40}
                />
                <StyledButton
                  icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                  onClick={handleRefresh}
                  loading={refreshLoading}
                  size="large"
                >
                  Refresh
                </StyledButton>
                <StyledButton
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  onClick={handleExport}
                  loading={exportLoading}
                  size="large"
                >
                  Export
                </StyledButton>
              </ControlsRow>
            </HeaderRow>
            {merchantData && (
              <StyledAlert
                message={`${MESSAGES.WELCOME_PREFIX} ${merchantData.name}!`}
                description={`${MESSAGES.LAST_UPDATED_PREFIX} ${
                  lastUpdated || "recently"
                }. ${MESSAGES.LAST_UPDATED_SUFFIX}`}
                type="info"
                showIcon
              />
            )}
          </div>
          <StyledTabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={
              TAB_CONFIG.map((tab) => {
                if (tab.key === "overview") {
                  return {
                    key: tab.key,
                    label: tab.label,
                    children: (
                      <TilesGrid gutter={[24, 24]}>
                        {overviewTiles.map((tile) => (
                          <Col key={tile.id} xs={24} sm={12} md={8} lg={8}>
                            <AnalyticsTile
                              title={tile.title}
                              icon={tile.icon}
                              value={tile.value}
                              trend={tile.change >= 0 ? "up" : "down"}
                              trendValue={Math.abs(tile.change)}
                              color={COLOR_MAP[tile.color] || tile.color}
                              subtitle={tile.description}
                              onClick={() => handleTileClick(tile)}
                              accentColor={COLOR_MAP[tile.color] || tile.color}
                              loading={overviewLoading || loading || analyticsLoading}
                              progress={tile.progress}
                            />
                          </Col>
                        ))}
                      </TilesGrid>
                    ),
                  };
                }
                const chartConfig = CHART_CONFIGS[tab.key];
                if (chartConfig) {
                  const chartComponents: Record<
                    string,
                    React.ComponentType<any>
                  > = {
                    salesTrends: SalesTrendChart,
                    categories: CategoryDistributionChart,
                    topItems: TopItemsChart,
                  };
                  const ChartComponent = chartComponents[tab.key];
                  if (ChartComponent) {
                    return {
                      key: tab.key,
                      label: tab.label,
                      children: (
                        <ChartCardWrapper
                          config={chartConfig}
                          onViewDetails={(tileId) =>
                            handleTileClick({ id: tileId } as AnalyticsTileType)
                          }
                        >
                          <ChartComponent
                            analyticsData={analyticsData}
                            salesTrendsData={salesTrendsData}
                            categoriesData={categoriesData}
                            topItemsData={topItemsData}
                            isDarkMode={isDarkMode}
                            loading={
                              tab.key === 'salesTrends' 
                                ? salesTrendsLoading 
                                : tab.key === 'categories' 
                                ? categoriesLoading 
                                : tab.key === 'topItems'
                                ? topItemsLoading
                                : false
                            }
                          />
                        </ChartCardWrapper>
                      ),
                    };
                  }
                }
                return null;
              }).filter(Boolean) as any
            }
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default MerchantAnalytics;